import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderedSet } from 'immutable';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, take } from 'rxjs/operators';
import { RouterService } from 'src/app/shared/shared-base/amg-ui/router.service';
import { AppState } from 'src/store/appstate';
import {
  selectRegionByCardNumber,
  selectRegionById,
  selectRegionsByCommunicationCenterId,
} from 'src/store/entityStores/region-selectors';
import { CommunicationCenterEntityFetchService } from 'src/store/generated/client-sync.generated';
import {
  CommunicationCenterRecord,
  CommunicationCenterViewModelBase,
  selectCommunicationCenterById,
} from 'src/store/generated/communication-center-record.generated';
import { selectQueryParam, selectRouteParam } from 'src/store/router/router-selectors';
import { undefinedOrMap, undefinedOrSwitchMap, valueOrSwitchMap } from 'src/util/rxjs';

export function observeCommunicationCenterFromRouteNavigationEnd(
  router: RouterService,
  store: Store<AppState>,
  extraRouteParams?: ('regionId' | 'cardNumber')[] | 'all',
) {
  return router.navigationEnd$.pipe(
    startWith(null),
    switchMap(() => observeCommunicationCenterFromRoute(store, extraRouteParams).pipe(take(1))),
    distinctUntilChanged((c1, c2) => c1?.id === c2?.id),
  );
}

export function observeCommunicationCenterFromRoute(
  store: Store<AppState>,
  extraRouteParams?: ('regionId' | 'cardNumber')[] | 'all',
) {
  const watchAll = extraRouteParams === 'all';
  const commCenterIdByCardNumber$ =
    watchAll || (extraRouteParams?.includes('cardNumber') ?? false)
      ? store.select(selectRouteParam('cardNumber')).pipe(
          undefinedOrSwitchMap((cardNumber) => store.select(selectRegionByCardNumber(cardNumber))),
          undefinedOrMap((region) => region.communicationCenterId),
        )
      : of(undefined);
  const commCenterIdByRegionId$ =
    watchAll || (extraRouteParams?.includes('regionId') ?? false)
      ? store.select(selectRouteParam('regionId')).pipe(
          undefinedOrSwitchMap((regionId) => store.select(selectRegionById(regionId))),
          undefinedOrMap((region) => region.communicationCenterId),
        )
      : of(undefined);
  const commCenterIdByQueryParam$ = store.select(selectQueryParam('communicationCenterId'));
  return commCenterIdByQueryParam$.pipe(
    valueOrSwitchMap(() => commCenterIdByRegionId$),
    valueOrSwitchMap(() => commCenterIdByCardNumber$),
    distinctUntilChanged(),
    undefinedOrSwitchMap((ccId) => store.select(selectCommunicationCenterById(ccId)).pipe(take(1))),
    undefinedOrMap((commCenter) => new CommunicationCenterViewModel(commCenter, store)),
  );
}

export class CommunicationCenterViewModel extends CommunicationCenterViewModelBase {
  public regionIds$: Observable<OrderedSet<string>>;
  constructor(commCenter: CommunicationCenterRecord, store: Store<AppState>) {
    super(commCenter);
    this.regionIds$ = store.select(selectRegionsByCommunicationCenterId(this.id)).pipe(
      map((rs) =>
        rs
          .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
          .map((r) => r.id)
          .toOrderedSet(),
      ),
      distinctUntilChanged(),
    );
  }
}

@Injectable({ providedIn: 'root' })
export class CommunicationCenterViewModelService {
  constructor(
    private readonly _communicationCenterFetchService: CommunicationCenterEntityFetchService,
    private store: Store<AppState>,
  ) {}

  public getById(id: string): Observable<CommunicationCenterViewModel | undefined> {
    return this._communicationCenterFetchService.getById(id).pipe(
      map((communicationCenter) => {
        if (communicationCenter == null) {
          return undefined;
        }
        return new CommunicationCenterViewModel(communicationCenter, this.store);
      }),
    );
  }
}
